import { useEffect, useState } from "react";
import { AlertTriangle, FileCheck2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PartnerAPI } from "@/lib/partnerApi";
import { formatApiError } from "@/context/PartnerAuthContext";

/**
 * Lightweight amendment-acknowledgment banner. If the current operator
 * version differs from the user's signed/acknowledged version, show a
 * callout listing the material changes with an "I've reviewed" button.
 * Original signature stands — this just creates an audit-trail ping.
 */
export default function AmendmentBanner() {
    const [status, setStatus] = useState(null);
    const [acking, setAcking] = useState(false);

    const load = async () => {
        try {
            setStatus(await PartnerAPI.amendmentStatus());
        } catch {
            setStatus({ needs_acknowledgment: false });
        }
    };
    useEffect(() => {
        load();
    }, []);

    if (!status || !status.needs_acknowledgment) return null;

    const ack = async () => {
        setAcking(true);
        try {
            await PartnerAPI.acknowledgeAmendments(status.current_version);
            toast.success("Updated terms acknowledged · audit trail logged");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setAcking(false);
        }
    };

    return (
        <div
            className="mb-10 p-5 bg-gold/15 border border-gold rounded-sm backdrop-blur"
            data-testid="partner-amendment-banner"
        >
            <div className="flex items-start gap-3 flex-wrap">
                <AlertTriangle size={20} className="text-gold shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                    <p className="font-serif text-xl text-cream">
                        Your agreement has been updated
                    </p>
                    <p className="text-cream/70 text-sm mt-1">
                        We've refreshed the terms since you signed. Please
                        take a moment to review the material clarifications
                        below:
                    </p>
                    <ul className="mt-3 space-y-1.5 text-cream/80 text-sm">
                        {status.material_changes.map((m) => (
                            <li
                                key={m}
                                className="flex items-start gap-2"
                                data-testid="partner-amendment-change-item"
                            >
                                <span className="text-gold shrink-0">·</span>
                                {m}
                            </li>
                        ))}
                    </ul>
                    <p className="text-cream/55 text-xs mt-3">
                        Your original signature stands — this is a quick
                        acknowledgment, not a re-signing.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={ack}
                    disabled={acking}
                    data-testid="partner-amendment-ack-btn"
                    className="btn-pill btn-gold text-xs shrink-0 disabled:opacity-50"
                >
                    {acking ? (
                        <Loader2 size={13} className="animate-spin" />
                    ) : (
                        <>
                            <FileCheck2 size={13} /> I've reviewed the updated terms
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
